<template>
    <!-- Dialog component -->
    <VJoyDialog
        ref="changeOwnerDialogRef"
        data-testid="change-quote-owner-overlay"
        :cancel-text="$t('generic.form.cancel.label')"
        size="large"
        @dialog:hide="handleResetValues"
    >
        <template #dialog-header>
            <h3>{{ $t('projectclient.quote.view.project-team.dialog.project-owner.search.label', {0: companyName}) }}</h3>
        </template>
        <template #dialog-body>
            <OverlayContent
                v-model:new-identity-id="state.newIdentityId"
                v-model:invitation-email="r$.$value.invitationEmail"
                v-model:search-query="searchQuery"
                :current-quote-owner="currentQuoteOwner"
                :filtered-members="filteredMembers"
                :email-errors="r$.invitationEmail.$errors"
                :new-identity-id-errors="r$.newIdentityId.$errors"
            />
        </template>
        <template #dialog-confirm>
            <VJoySubmit
                :disabled="submitDisabledConfig.disabled"
                :tooltip="submitDisabledConfig.reason"
                data-testid="confirm-quote-owner-change-button"
                :action="handleOwnerChange"
            >
                {{ $t('generic.form.confirm.label') }}
            </VJoySubmit>
        </template>
    </VJoyDialog>
    <!-- Bottomsheet component -->
    <VJoyBottomSheet ref="changeOwnerBottomSheetRef" data-testid="change-quote-owner-overlay" @bottom-sheet:close="handleResetValues">
        <template #bottom-sheet-content>
            <h3 class="joy-font-primary-650">
                {{ $t('projectclient.quote.view.project-team.dialog.project-owner.search.label', {0: companyName}) }}
            </h3>
            <OverlayContent
                v-model:new-identity-id="state.newIdentityId"
                v-model:invitation-email="r$.$value.invitationEmail"
                v-model:search-query="searchQuery"
                :current-quote-owner="currentQuoteOwner"
                :filtered-members="filteredMembers"
                :email-errors="r$.invitationEmail.$errors"
                :new-identity-id-errors="r$.newIdentityId.$errors"
            />
        </template>
        <template #bottom-sheet-cta>
            <div class="bottom-sheet__footer">
                <VJoySubmit
                    :disabled="submitDisabledConfig.disabled"
                    :tooltip="submitDisabledConfig.reason"
                    data-testid="confirm-quote-owner-change-button"
                    :action="handleOwnerChange"
                >
                    {{ $t('generic.form.confirm.label') }}
                </VJoySubmit>
            </div>
        </template>
    </VJoyBottomSheet>
</template>

<script setup lang="ts">
    import {ref, useTemplateRef, computed, watch} from 'vue';
    import {eventBus} from '@common/events';
    import {useScreenSizeStore} from '@common/stores';
    import type {Maybe} from '@regle/core';
    import {email, isFilled, maxLength, requiredIf, withMessage} from '@regle/rules';
    import {storeToRefs} from 'pinia';
    import {VJoySubmit} from '#components';
    import {useTranslation} from '#imports';
    import {useRegle, validateForm} from '@malt/forms';
    import {VJoyDialog, VJoyBottomSheet} from '@maltjoy/core-vue';
    import OverlayContent from './OverlayContent.vue';
    import {useCompanyInfoStore, useClientQuoteViewStore} from '@/features/quote/stores';

    const props = defineProps<{
        currentQuoteOwner: string;
        quoteId: string;
    }>();

    const {t} = useTranslation();

    const screenSizeStore = useScreenSizeStore();
    const {isDesktopScreen} = storeToRefs(screenSizeStore);

    const companyInfoStore = useCompanyInfoStore();
    const {companyMembers} = storeToRefs(companyInfoStore);

    const quoteViewStore = useClientQuoteViewStore();
    const {quote} = storeToRefs(quoteViewStore);

    const companyName = computed(() => quote.value?.client.company.name ?? '');

    const filteredMembers = computed(() => {
        return (
            companyMembers.value &&
            companyMembers.value.filter((member) => {
                const fullName = `${member.firstName} ${member.lastName}`.toLowerCase();
                return fullName.includes(searchQuery.value.toLowerCase()) && fullName !== props.currentQuoteOwner.toLowerCase();
            })
        );
    });

    const submitDisabledConfig = computed<{disabled: boolean; reason?: string}>(() => {
        if (!state.value.newIdentityId && !r$.$value.invitationEmail) {
            return {disabled: true, reason: t('projectclient.quote.view.project-team.dialog.project-owner.tooltip.submit-disabled')};
        }
        return {disabled: false};
    });

    // trigger either dialog or bottom sheet
    const changeOwnerDialogRef = useTemplateRef('changeOwnerDialogRef');
    const changeOwnerBottomSheetRef = useTemplateRef('changeOwnerBottomSheetRef');

    eventBus.on('openChangeQuoteOwnerModal', () => {
        if (isDesktopScreen.value) {
            changeOwnerDialogRef.value?.show();
        } else {
            changeOwnerBottomSheetRef.value?.show();
        }
    });

    function closeChangeQuoteOwnerModal() {
        if (isDesktopScreen.value) {
            changeOwnerDialogRef.value?.hide();
        } else {
            changeOwnerBottomSheetRef.value?.hide();
        }
    }

    // handle inputs + confirm owner change
    const state = ref({
        invitationEmail: '',
        newIdentityId: '',
    });
    const searchQuery = ref('');

    const {r$} = useRegle(
        state,
        {
            invitationEmail: {
                alreadyRegistered: withMessage((value: Maybe<string>) => {
                    if (isFilled(value) && email.exec(value)) return !emailAlreadyRegistered(value);
                    return true;
                }, t('projectclient.quote.view.project-team.dialog.project-owner.email.error.already-registered')),
                required: requiredIf(() => !state.value.newIdentityId),
                maxLength: maxLength(200),
                email,
            },
            newIdentityId: {
                required: requiredIf(() => !state.value.invitationEmail),
            },
        },
        {
            rewardEarly: true,
        },
    );

    function findNewManagerName(newIdentityId: string) {
        const newManager = companyMembers.value?.find((member) => member.identityId === newIdentityId);
        return `${newManager?.firstName} ${newManager?.lastName}`;
    }

    async function handleOwnerChange() {
        const {data, valid} = await validateForm(r$);
        if (!valid) return;

        if (data.newIdentityId) {
            const newManagerName = findNewManagerName(data.newIdentityId) ?? '';
            await quoteViewStore.updateQuoteOwner({quoteId: props.quoteId, newIdentityId: data.newIdentityId, newManagerName});
            await quoteViewStore.getQuote(props.quoteId);
            closeChangeQuoteOwnerModal();
        } else if (data.invitationEmail) {
            await quoteViewStore.updateQuoteOwner({quoteId: props.quoteId, invitationEmail: data.invitationEmail});
            await quoteViewStore.getQuote(props.quoteId);
            closeChangeQuoteOwnerModal();
        }
    }

    function emailAlreadyRegistered(newEmail: string): boolean {
        return companyMembers.value?.some((member) => member.email === newEmail) || false;
    }

    function handleResetValues() {
        searchQuery.value = '';
        r$.$reset({toOriginalState: true});
    }

    watch(
        () => r$.$value.invitationEmail,
        (newVal) => {
            if (newVal !== '') {
                state.value.newIdentityId = '';
            }
        },
        {immediate: true, deep: true},
    );

    watch(
        () => state.value.newIdentityId,
        (newVal) => {
            if (newVal !== '') {
                state.value.invitationEmail = '';
                r$.$reset();
            }
        },
    );
</script>
